import type { HeartRateEvent } from '@model/DeviceConfiguration';
import { RESAMPLE_HZ, preprocessHrvPath } from '../hrv-preprocessing';
import type { PsdSeries } from '../../types';
import { computeWelchPsd } from './computeWelchPsd';
import { WELCH_WINDOW } from './constants';

export type SegmentPsdResult =
  | { ok: true; psd: PsdSeries }
  | { ok: false; reason: string };

const MIN_RESAMPLED_SAMPLES = WELCH_WINDOW;

export const buildSegmentHrSeries = (
  events: HeartRateEvent[]
): { hrValues: number[]; timestamps: number[] } => {
  const sorted = [...events].sort(
    (left, right) => left.timestamp - right.timestamp
  );

  const hrValues: number[] = [];
  const timestamps: number[] = [];

  sorted.forEach(event => {
    const hr = event.hr;
    if (typeof hr !== 'number' || !Number.isFinite(hr) || hr <= 0) return;

    hrValues.push(hr);
    timestamps.push(event.timestamp);
  });

  return { hrValues, timestamps };
};

export const computeEventsSegmentPsd = (
  events: HeartRateEvent[]
): SegmentPsdResult => {
  if (events.length === 0) {
    return { ok: false, reason: 'Nessun evento HR nel tratto selezionato.' };
  }

  const { hrValues, timestamps } = buildSegmentHrSeries(events);

  if (hrValues.length < 10) {
    return {
      ok: false,
      reason: 'Eventi HR insufficienti nel tratto selezionato (< 10).',
    };
  }

  const preprocessed = preprocessHrvPath({ hrValues, timestamps });

  if (!preprocessed.passed) {
    return {
      ok: false,
      reason:
        preprocessed.logs.at(-1) ??
        'Preprocessing HRV non superato per questo tratto.',
    };
  }

  if (preprocessed.resampled4Hz.length < MIN_RESAMPLED_SAMPLES) {
    return {
      ok: false,
      reason: `Tratto troppo corto per la PSD Welch (minimo ~${Math.ceil(MIN_RESAMPLED_SAMPLES / RESAMPLE_HZ)} s preprocessati).`,
    };
  }

  const psd = computeWelchPsd(preprocessed.resampled4Hz, RESAMPLE_HZ);

  if (psd.frequencies.length === 0) {
    return { ok: false, reason: 'Impossibile calcolare la PSD sul tratto.' };
  }

  return { ok: true, psd };
};
